/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useCallback, useEffect, useState } from 'react'
import * as z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { api, getUserModels } from '@/lib/api'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { SettingsSection } from '../components/settings-section'
import { useResetForm } from '../hooks/use-reset-form'
import { useUpdateOption } from '../hooks/use-update-option'
import { isTextModel } from '../utils/text-models'

// ---------------------------------------------------------------------------
// Part A — Config form
// ---------------------------------------------------------------------------

const supportSchema = z.object({
  SupportChatEnabled: z.boolean(),
  SupportChatModel: z.string(),
  SupportChatFallbackModel: z.string(),
  SupportContact: z.string(),
  SupportRetrievalMode: z.enum(['keyword', 'embedding']),
  SupportEmbeddingModel: z.string(),
  SupportTopK: z.coerce.number().min(1),
  SupportSystemPrompt: z.string(),
})

export type SupportKbFormValues = z.infer<typeof supportSchema>

type SupportKbSectionProps = {
  defaultValues: SupportKbFormValues
}

function SupportConfigForm({ defaultValues }: SupportKbSectionProps) {
  const { t } = useTranslation()
  const updateOption = useUpdateOption()

  const form = useForm<SupportKbFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(supportSchema) as any,
    defaultValues,
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useResetForm(form as any, defaultValues)

  const retrievalMode = form.watch('SupportRetrievalMode')

  // Text/chat models available to the current account, for the model pickers.
  const [modelOptions, setModelOptions] = useState<string[]>([])
  useEffect(() => {
    getUserModels()
      .then((r) => setModelOptions((r.data ?? []).filter(isTextModel).sort()))
      .catch(() => {})
  }, [])

  const onSubmit = async (values: SupportKbFormValues) => {
    const entries = Object.entries(values) as [
      keyof SupportKbFormValues,
      unknown,
    ][]
    const updates = entries.filter(
      ([key, value]) => value !== (defaultValues[key] as unknown)
    )
    if (updates.length === 0) {
      toast.info(t('No changes to save'))
      return
    }
    for (const [key, value] of updates) {
      await updateOption.mutateAsync({
        key,
        value: value as string | number | boolean,
      })
    }
  }

  return (
    <SettingsSection
      title={t('Support Knowledge Base')}
      description={t(
        'Configure the AI customer-service assistant and review captured Q&A'
      )}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
          <FormField
            control={form.control}
            name='SupportChatEnabled'
            render={({ field }) => (
              <FormItem className='border-border/60 bg-card hover:bg-accent/20 flex flex-row items-center justify-between rounded-lg border p-4 transition-colors'>
                <div className='space-y-0.5'>
                  <FormLabel className='text-base'>
                    {t('Enable AI customer service')}
                  </FormLabel>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <div className='grid gap-6 md:grid-cols-2'>
            <FormField
              control={form.control}
              name='SupportChatModel'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Primary model')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className='w-full font-mono'>
                        <SelectValue placeholder='gemini-3.5-flash-google-search' />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent alignItemWithTrigger={false}>
                      <SelectGroup>
                        {field.value && !modelOptions.includes(field.value) && (
                          <SelectItem value={field.value}>
                            {field.value}
                          </SelectItem>
                        )}
                        {modelOptions.map((m) => (
                          <SelectItem key={m} value={m}>
                            {m}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='SupportChatFallbackModel'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Fallback model')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className='w-full font-mono'>
                        <SelectValue placeholder='gpt-5.4-mini' />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent alignItemWithTrigger={false}>
                      <SelectGroup>
                        {field.value && !modelOptions.includes(field.value) && (
                          <SelectItem value={field.value}>
                            {field.value}
                          </SelectItem>
                        )}
                        {modelOptions.map((m) => (
                          <SelectItem key={m} value={m}>
                            {m}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='SupportContact'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Human fallback contact')}</FormLabel>
                  <FormControl>
                    <Input placeholder='support@aiproxys.net' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='SupportRetrievalMode'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Retrieval mode')}</FormLabel>
                  <Select
                    items={[
                      { value: 'keyword', label: t('Keyword') },
                      { value: 'embedding', label: t('Embedding') },
                    ]}
                    value={field.value}
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger className='w-full'>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent alignItemWithTrigger={false}>
                      <SelectGroup>
                        <SelectItem value='keyword'>{t('Keyword')}</SelectItem>
                        <SelectItem value='embedding'>
                          {t('Embedding')}
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='SupportEmbeddingModel'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('Embedding model (for semantic retrieval)')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={retrievalMode !== 'embedding'}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='SupportTopK'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Retrieved chunks (Top-K)')}</FormLabel>
                  <FormControl>
                    <Input type='number' min={1} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name='SupportSystemPrompt'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('System prompt')}</FormLabel>
                <FormControl>
                  <Textarea rows={6} {...field} />
                </FormControl>
                <FormDescription>
                  {t(
                    'Blank uses the built-in default. Placeholders {{contact}} and {{lang}} are supported.'
                  )}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type='submit' disabled={updateOption.isPending}>
            {updateOption.isPending
              ? t('Saving...')
              : t('Save support settings')}
          </Button>
        </form>
      </Form>
    </SettingsSection>
  )
}

// ---------------------------------------------------------------------------
// Part B — Knowledge base rebuild + stats
// ---------------------------------------------------------------------------

type KbStats = {
  docs?: number
  news?: number
  qa?: number
  booth?: number
} & Record<string, number>

type ApiEnvelope<T> = {
  success: boolean
  message?: string
  data: T
}

function KnowledgeBasePanel() {
  const { t } = useTranslation()
  const [stats, setStats] = useState<KbStats | null>(null)
  const [rebuilding, setRebuilding] = useState(false)

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get<ApiEnvelope<KbStats>>('/api/support/kb/stats')
      if (res.data.success) setStats(res.data.data)
    } catch {
      /* handled by global interceptor */
    }
  }, [])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const rebuild = async () => {
    setRebuilding(true)
    try {
      const res = await api.post<ApiEnvelope<KbStats>>(
        '/api/support/kb/rebuild',
        {}
      )
      if (res.data.success) {
        const d = res.data.data
        toast.success(
          t('Knowledge base rebuilt: {{docs}} docs, {{news}} news', {
            docs: d.docs ?? 0,
            news: d.news ?? 0,
          })
        )
        fetchStats()
      }
    } catch {
      /* handled by global interceptor */
    } finally {
      setRebuilding(false)
    }
  }

  return (
    <SettingsSection
      title={t('Knowledge base')}
      description={t('Source counts and index rebuild')}
    >
      <div className='space-y-4'>
        <div className='grid grid-cols-2 gap-3 md:grid-cols-4'>
          <StatTile label={t('Documents')} value={stats?.docs} />
          <StatTile label={t('News')} value={stats?.news} />
          <StatTile label={t('Q&A')} value={stats?.qa} />
          <StatTile label={t('Booth')} value={stats?.booth} />
        </div>
        <Button onClick={rebuild} disabled={rebuilding}>
          {rebuilding ? t('Rebuilding...') : t('Rebuild knowledge base')}
        </Button>
      </div>
    </SettingsSection>
  )
}

function StatTile({ label, value }: { label: string; value?: number }) {
  return (
    <div className='rounded-lg border p-4'>
      <p className='text-muted-foreground text-xs'>{label}</p>
      <p className='mt-1 text-lg font-semibold'>{value ?? '-'}</p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Part C — Q&A review queue
// ---------------------------------------------------------------------------

type QaStatusFilter = 'pending' | 'approved' | 'rejected' | 'all'

type QaItem = {
  id: number
  question: string
  answer: string
  lang: string
  page_url: string
  status: string
  created_at: number | string
}

type QaListData = {
  items: QaItem[]
  total: number
  page: number
  size: number
}

const PAGE_SIZE = 20

function QaReviewQueue() {
  const { t } = useTranslation()
  const [status, setStatus] = useState<QaStatusFilter>('pending')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [items, setItems] = useState<QaItem[]>([])
  const [loading, setLoading] = useState(false)
  const [busyId, setBusyId] = useState<number | null>(null)

  const fetchList = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get<ApiEnvelope<QaListData>>('/api/support/qa', {
        params: { status, page, size: PAGE_SIZE },
      })
      if (res.data.success) {
        setItems(res.data.data.items ?? [])
        setTotal(res.data.data.total ?? 0)
      }
    } catch {
      /* handled by global interceptor */
    } finally {
      setLoading(false)
    }
  }, [status, page])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  const patchItem = (id: number, patch: Partial<QaItem>) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...patch } : item))
    )
  }

  const submitAction = async (
    item: QaItem,
    action: 'approve' | 'reject' | 'save'
  ) => {
    setBusyId(item.id)
    try {
      const body =
        action === 'reject'
          ? { action }
          : { question: item.question, answer: item.answer, action }
      const res = await api.put<ApiEnvelope<unknown>>(
        `/api/support/qa/${item.id}`,
        body
      )
      if (res.data.success) {
        toast.success(t('Saved successfully'))
        fetchList()
      }
    } catch {
      /* handled by global interceptor */
    } finally {
      setBusyId(null)
    }
  }

  const remove = async (id: number) => {
    setBusyId(id)
    try {
      const res = await api.delete<ApiEnvelope<unknown>>(
        `/api/support/qa/${id}`
      )
      if (res.data.success) {
        toast.success(t('Deleted successfully'))
        fetchList()
      }
    } catch {
      /* handled by global interceptor */
    } finally {
      setBusyId(null)
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const changeStatus = (next: QaStatusFilter) => {
    setStatus(next)
    setPage(1)
  }

  return (
    <SettingsSection
      title={t('Q&A review queue')}
      description={t('Review, edit, approve or reject captured answers')}
    >
      <div className='space-y-4'>
        <div className='flex items-end gap-3'>
          <div className='grid gap-1.5'>
            <Label className='text-xs'>{t('Status')}</Label>
            <Select
              items={[
                { value: 'pending', label: t('Pending review') },
                { value: 'approved', label: t('Approved') },
                { value: 'rejected', label: t('Rejected') },
                { value: 'all', label: t('All') },
              ]}
              value={status}
              onValueChange={(v) =>
                v !== null && changeStatus(v as QaStatusFilter)
              }
            >
              <SelectTrigger className='w-[160px]'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent alignItemWithTrigger={false}>
                <SelectGroup>
                  <SelectItem value='pending'>{t('Pending review')}</SelectItem>
                  <SelectItem value='approved'>{t('Approved')}</SelectItem>
                  <SelectItem value='rejected'>{t('Rejected')}</SelectItem>
                  <SelectItem value='all'>{t('All')}</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <Button variant='outline' size='sm' onClick={fetchList}>
            {t('Refresh')}
          </Button>
        </div>

        {loading ? (
          <p className='text-muted-foreground text-sm'>{t('Loading...')}</p>
        ) : items.length === 0 ? (
          <p className='text-muted-foreground text-sm'>{t('No data')}</p>
        ) : (
          <div className='space-y-4'>
            {items.map((item) => {
              const busy = busyId === item.id
              return (
                <div key={item.id} className='space-y-3 rounded-lg border p-4'>
                  <div className='text-muted-foreground flex flex-wrap gap-3 text-xs'>
                    <span>#{item.id}</span>
                    <span>{item.lang}</span>
                    <span>{item.status}</span>
                    {item.page_url && (
                      <span className='truncate'>{item.page_url}</span>
                    )}
                    <span>{String(item.created_at)}</span>
                  </div>
                  <div className='grid gap-1.5'>
                    <Label className='text-xs'>{t('Question')}</Label>
                    <Textarea
                      rows={2}
                      value={item.question}
                      onChange={(e) =>
                        patchItem(item.id, { question: e.target.value })
                      }
                    />
                  </div>
                  <div className='grid gap-1.5'>
                    <Label className='text-xs'>{t('Answer')}</Label>
                    <Textarea
                      rows={4}
                      value={item.answer}
                      onChange={(e) =>
                        patchItem(item.id, { answer: e.target.value })
                      }
                    />
                  </div>
                  <div className='flex flex-wrap gap-2'>
                    <Button
                      size='sm'
                      disabled={busy}
                      onClick={() => submitAction(item, 'approve')}
                    >
                      {t('Approve')}
                    </Button>
                    <Button
                      size='sm'
                      variant='outline'
                      disabled={busy}
                      onClick={() => submitAction(item, 'save')}
                    >
                      {t('Save')}
                    </Button>
                    <Button
                      size='sm'
                      variant='outline'
                      disabled={busy}
                      onClick={() => submitAction(item, 'reject')}
                    >
                      {t('Reject')}
                    </Button>
                    <Button
                      size='sm'
                      variant='destructive'
                      disabled={busy}
                      onClick={() => remove(item.id)}
                    >
                      {t('Delete')}
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className='flex items-center justify-between'>
            <span className='text-muted-foreground text-xs'>
              {t('Page {{page}} of {{total}}', { page, total: totalPages })}
            </span>
            <div className='flex gap-2'>
              <Button
                variant='outline'
                size='sm'
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t('Previous')}
              </Button>
              <Button
                variant='outline'
                size='sm'
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('Next')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </SettingsSection>
  )
}

// ---------------------------------------------------------------------------
// Part D — Conversations & captured leads
// ---------------------------------------------------------------------------

type SupportSession = {
  id: number
  session_id: string
  name: string
  email: string
  phone: string
  social: string
  lang: string
  page_url: string
  turn_count: number
  created_at: number
  updated_at: number
}

type SupportTurn = {
  id: number
  session_id: string
  question: string
  answer: string
  lang: string
  page_url: string
  created_at: number
}

type SessionListData = {
  items: SupportSession[]
  total: number
  page: number
  size: number
}

function formatUnixSeconds(seconds: number): string {
  if (!seconds) return '-'
  return new Date(seconds * 1000).toLocaleString()
}

function ConversationsPanel() {
  const { t } = useTranslation()
  const [onlyEmail, setOnlyEmail] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [items, setItems] = useState<SupportSession[]>([])
  const [loading, setLoading] = useState(false)
  const [active, setActive] = useState<SupportSession | null>(null)

  const fetchList = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get<ApiEnvelope<SessionListData>>(
        '/api/support/conversations',
        { params: { has_email: onlyEmail, page, size: PAGE_SIZE } }
      )
      if (res.data.success) {
        setItems(res.data.data.items ?? [])
        setTotal(res.data.data.total ?? 0)
      }
    } catch {
      /* handled by global interceptor */
    } finally {
      setLoading(false)
    }
  }, [onlyEmail, page])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const toggleOnlyEmail = (checked: boolean) => {
    setOnlyEmail(checked)
    setPage(1)
  }

  return (
    <SettingsSection
      title={t('Conversations')}
      description={t('Browse customer conversations and captured lead info')}
    >
      <div className='space-y-4'>
        <div className='flex items-center gap-3'>
          <Switch
            id='support-only-email'
            checked={onlyEmail}
            onCheckedChange={toggleOnlyEmail}
          />
          <Label htmlFor='support-only-email' className='text-sm'>
            {t('Only with email')}
          </Label>
          <Button variant='outline' size='sm' onClick={fetchList}>
            {t('Refresh')}
          </Button>
        </div>

        {loading ? (
          <p className='text-muted-foreground text-sm'>{t('Loading...')}</p>
        ) : items.length === 0 ? (
          <p className='text-muted-foreground text-sm'>
            {t('No conversations yet')}
          </p>
        ) : (
          <div className='rounded-lg border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('Email')}</TableHead>
                  <TableHead>{t('Name')}</TableHead>
                  <TableHead>{t('Phone')}</TableHead>
                  <TableHead>{t('Social')}</TableHead>
                  <TableHead>{t('Turns')}</TableHead>
                  <TableHead>{t('Language')}</TableHead>
                  <TableHead>{t('Updated')}</TableHead>
                  <TableHead className='text-right'>{t('Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((session) => (
                  <TableRow key={session.id}>
                    <TableCell>{session.email || '-'}</TableCell>
                    <TableCell>{session.name || '-'}</TableCell>
                    <TableCell>{session.phone || '-'}</TableCell>
                    <TableCell className='max-w-[160px] truncate'>
                      {session.social || '-'}
                    </TableCell>
                    <TableCell>{session.turn_count}</TableCell>
                    <TableCell>{session.lang || '-'}</TableCell>
                    <TableCell>
                      {formatUnixSeconds(session.updated_at)}
                    </TableCell>
                    <TableCell className='text-right'>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => setActive(session)}
                      >
                        {t('View conversation')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className='flex items-center justify-between'>
            <span className='text-muted-foreground text-xs'>
              {t('Page {{page}} of {{total}}', { page, total: totalPages })}
            </span>
            <div className='flex gap-2'>
              <Button
                variant='outline'
                size='sm'
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t('Previous')}
              </Button>
              <Button
                variant='outline'
                size='sm'
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('Next')}
              </Button>
            </div>
          </div>
        )}
      </div>

      <ConversationThreadDialog
        session={active}
        onClose={() => setActive(null)}
      />
    </SettingsSection>
  )
}

function ConversationThreadDialog({
  session,
  onClose,
}: {
  session: SupportSession | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [turns, setTurns] = useState<SupportTurn[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!session) return
    let cancelled = false
    setTurns([])
    setLoading(true)
    api
      .get<ApiEnvelope<SupportTurn[]>>(
        `/api/support/conversations/${session.session_id}`
      )
      .then((res) => {
        if (!cancelled && res.data.success) setTurns(res.data.data ?? [])
      })
      .catch(() => {
        /* handled by global interceptor */
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [session])

  const contactParts = session
    ? [session.name, session.email, session.phone, session.social].filter(
        Boolean
      )
    : []

  return (
    <Dialog open={session !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='max-h-[85vh] overflow-hidden sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>{t('View conversation')}</DialogTitle>
          <div className='text-muted-foreground text-xs'>
            <span className='font-medium'>{t('Contact info')}: </span>
            {contactParts.length > 0 ? contactParts.join(' · ') : '-'}
          </div>
        </DialogHeader>

        <div className='max-h-[70vh] space-y-3 overflow-y-auto py-2 pr-1'>
          {loading ? (
            <p className='text-muted-foreground text-sm'>{t('Loading...')}</p>
          ) : turns.length === 0 ? (
            <p className='text-muted-foreground text-sm'>{t('No data')}</p>
          ) : (
            turns.map((turn) => (
              <div key={turn.id} className='space-y-2'>
                <div className='flex justify-end'>
                  <div className='bg-primary text-primary-foreground max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap'>
                    {turn.question}
                  </div>
                </div>
                <div className='flex justify-start'>
                  <div className='bg-muted max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap'>
                    {turn.answer}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------

export function SupportKbSection({ defaultValues }: SupportKbSectionProps) {
  return (
    <div className='space-y-8'>
      <SupportConfigForm defaultValues={defaultValues} />
      <Separator />
      <KnowledgeBasePanel />
      <Separator />
      <QaReviewQueue />
      <Separator />
      <ConversationsPanel />
    </div>
  )
}
